<template>
  <component :is="tag" ref="textRef" class="rich-text" nosem v-html="htmlValue"></component>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUpdated, PropType, ref } from 'vue';
import store from '@/store';
import { replaceHrefsWithUrlTrackingAndReplacementTags } from '@/utilities/Url';
import { ReplacementTags } from '@/store/modules/global/state';
import { applyReplacementTags, isSsr } from '@/utilities/Utilities';

export default defineComponent({
  name: 'RichTextRenderer',
  props: {
    html: {
      type: String
    },
    tag: {
      type: String,
      default: 'span'
    },
    extraReplacementTags: {
      type: Object as PropType<ReplacementTags>,
      required: false
    }
  },
  setup: (props) => {
    const textRef = ref<HTMLElement>();

    /**
     * TODO: We might need to find a way to handle internal links and scroll to section more gracefully
     */

    const htmlValue = computed(() => {
      if (props.html) {
        const html = props.html.replace(/&lt;/gi, '<').replace(/&gt;/gi, '>');

        if (!isSsr()) {
          // TODO: Refactor this. Why in the world are we instansiating HTML just to parse the anchor tags???
          let link: null | string = null;
          let temp = document.createElement('div');
          temp.innerHTML = html;
          const tempSelector = temp.querySelectorAll('a');
          let apply = applyReplacementTags(temp.innerHTML, props.extraReplacementTags);
          if (tempSelector) {
            tempSelector.forEach((selector) => {
              link = selector.getAttribute('href');
              if (link && link.includes('#/?') && !link.includes('section')) {
                const linkParts = link?.split('=');
                if (linkParts && linkParts.length > 0) {
                  selector?.classList.add('internal-link');
                  selector?.setAttribute('data-pageid', linkParts[1]);
                  apply = applyReplacementTags(temp.innerHTML, props.extraReplacementTags);
                }
              }
              if (link && link.includes('section')) {
                const linkParts = link?.split('=');
                if (linkParts && linkParts.length > 0 && selector) {
                  selector?.classList.add('section-link');
                  selector?.setAttribute('data-section', linkParts[1]);
                  apply = applyReplacementTags(temp.innerHTML, props.extraReplacementTags);
                }
              }
            });
            return replaceHrefsWithUrlTrackingAndReplacementTags(apply, props.extraReplacementTags);
          }

          /**
           * If the link is internal, we dont want to add tracking to the URL, as that cant be resolved
           * Instead, we want to open a popover, which we handle in the onMounted
           */
        }
        return replaceHrefsWithUrlTrackingAndReplacementTags(html, props.extraReplacementTags);
      }
      return '';
    });

    const applyEventListeners = () => {
      const links = textRef.value?.querySelectorAll('a');
      if (links && links.length > 0) {
        links.forEach((link) => {
          if (link.classList.contains('internal-link')) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const pageId = link.getAttribute('data-pageid');
              store.commit('campaign/setPopoverId', pageId);
            });
          }
          if (link.classList.contains('section-link')) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const sectionId = link.getAttribute('data-section');
              const el = document.querySelector(`.section--${sectionId}`);
              if (el) {
                el.scrollIntoView({
                  behavior: 'smooth'
                });
                return;
              }
            });
          }
        });
      }
    };

    onMounted(() => {
      applyEventListeners();
    });

    onUpdated(() => {
      applyEventListeners();
    });

    return { htmlValue, textRef };
  }
});
</script>
